'use client';

import { apiFetch } from '@/lib/utils/api';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { Button, Row, Stack } from '@slims/ui/atoms';
import {
	AdminSelect,
	CardBadge,
	ConfirmationDialog,
	InitialAvatar,
	InlineControlSkeleton,
	LabeledSelect,
	ListCard,
	ListCardEmpty,
	ListCardRow,
	ListCardSkeleton,
	StatusBlock,
} from '@slims/ui/molecules';
import { Layers, Pencil, Plus, Trash2, UserPlus, Users } from 'lucide-react';
import Link from '@/lib/next/link';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AddCerUser } from '@/components/admin/add-cer-user';
import { useAdminScopeTree } from '@/hooks/use-admin-scope';
import { adminScopeFor } from '@/lib/admin-scope';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

interface Cer {
	id: string;
	nome: string;
	tenantId: string;
	disabled?: boolean;
}

interface Config {
	id: string;
	nome: string;
	soci: number;
	membersCount: number;
	kwp: number;
	disabled?: boolean;
}

interface CerUser {
	id: string;
	email: string;
	fullName: string | null;
	role: string;
}

export default function ConfigurationsPage() {
	const adminCerId = useAdminScopeStore((s) => s.cerId);
	const setAdminScope = useAdminScopeStore((s) => s.setScope);
	const { tree: adminTree } = useAdminScopeTree();
	const [cers, setCers] = useState<Cer[]>([]);
	const [cerId, setCerId] = useState('');
	const [configs, setConfigs] = useState<Config[]>([]);
	const [configsLoading, setConfigsLoading] = useState(true);
	const [loading, setLoading] = useState(true);
	const [pendingDelete, setPendingDelete] = useState<Config | null>(null);
	const [deleting, setDeleting] = useState(false);

	const loadCers = useCallback(() => {
		return (
			apiFetch('/api/admin/my-cers')
				.then((r) => r.json())
				// Su errore l'API risponde `{ error }`: senza questo guard finiva in
				// `cers` e il render successivo esplodeva con `.map is not a function`.
				.then((data: unknown) => {
					const list = Array.isArray(data) ? (data as Cer[]) : [];
					setCers(list);
					if (list.length > 0) {
						// Letto al momento della risposta, non alla creazione della
						// callback: nel frattempo «Dettagli» o `useScopeSync` possono
						// averlo cambiato.
						const stored = useAdminScopeStore.getState().cerId;
						const fallback = list.some((c) => c.id === stored) ? (stored as string) : list[0].id;
						setCerId((prev) => prev || fallback);
					}
					setLoading(false);
				})
				.catch(() => setLoading(false))
		);
	}, []);

	// La CER di lavoro è una sola per tutta l'area admin (INB-331): se cambia
	// altrove (Soci, «Dettagli», una configurazione appena creata) la segue.
	// Fa eccezione una CER scelta qui che lo scope admin non può contenere
	// (disabilitata): resta finché non se ne sceglie un'altra.
	useEffect(() => {
		if (!adminCerId || !cers.some((c) => c.id === adminCerId)) return;
		setCerId((prev) =>
			prev &&
			prev !== adminCerId &&
			cers.some((c) => c.id === prev) &&
			!adminTree.some((c) => c.id === prev)
				? prev
				: adminCerId,
		);
	}, [adminCerId, cers, adminTree]);

	useEffect(() => {
		loadCers();
	}, [loadCers]);

	// A CER enabled/disabled or renamed from another tab (Super Admin) must
	// refresh this switcher without a manual reload.
	useEffect(() => {
		// Supabase Realtime filters only support eq/neq/lt/lte/gt/gte/in — there's
		// no `is.null` to isolate top-level CERs from configs at the channel
		// level, so this listens to every `organizations` row change and just
		// re-fetches; the endpoint itself already returns CERs only.
		return subscribeToTable(
			'organizations-cer-list',
			{ event: '*', schema: 'public', table: 'organizations' },
			() => {
				loadCers();
			},
		);
	}, [loadCers]);

	function selectCer(id: string) {
		setCerId(id);
		const next = adminScopeFor(adminTree, id, useAdminScopeStore.getState().configId);
		if (next) setAdminScope(next.cerId, next.configId);
	}

	const loadConfigs = useCallback(async () => {
		if (!cerId) {
			setConfigs([]);
			setConfigsLoading(false);
			return;
		}
		setConfigsLoading(true);
		try {
			const res = await apiFetch(`/api/admin/cers/${cerId}/configs`);
			if (!res.ok) return;
			const data: unknown = await res.json();
			setConfigs(Array.isArray(data) ? (data as Config[]) : []);
		} finally {
			setConfigsLoading(false);
		}
	}, [cerId]);

	useEffect(() => {
		loadConfigs();
	}, [loadConfigs]);

	// A configuration created/renamed/toggled/deleted (own tab or another) must
	// refresh this list live — soci count, kWp and disabled badge included.
	useEffect(() => {
		if (!cerId) return;
		return subscribeToTable(
			`organizations-configs-${cerId}`,
			{
				event: '*',
				schema: 'public',
				table: 'organizations',
				filter: `parent_id=eq.${cerId}`,
			},
			() => {
				loadConfigs();
			},
		);
	}, [cerId, loadConfigs]);

	async function confirmDelete() {
		if (!pendingDelete) return;
		setDeleting(true);
		try {
			const res = await apiFetch(`/api/admin/configs/${pendingDelete.id}`, { method: 'DELETE' });
			if (res.ok) {
				toast.success(`"${pendingDelete.nome}" eliminata`);
				setConfigs((prev) => prev.filter((c) => c.id !== pendingDelete.id));
				setPendingDelete(null);
			} else {
				toast.error("Errore durante l'eliminazione");
			}
		} finally {
			setDeleting(false);
		}
	}

	if (loading) {
		// Non si sa ancora quali CER amministra: niente selettore da mostrare
		// finto, ma la forma della pagina (selettore + due liste) resta la
		// stessa, così le card sotto non appaiono di colpo.
		return (
			<Stack gap="xl">
				<Row wrap justify="between" gap="md">
					<InlineControlSkeleton width="lg" />
					<InlineControlSkeleton width="md" />
				</Row>
				<ListCard header="Configurazioni">
					<ListCardSkeleton rows={3} />
				</ListCard>
				<ListCard
					header={
						<>
							<Users /> Referenti
						</>
					}
				>
					<ListCardSkeleton rows={2} />
				</ListCard>
			</Stack>
		);
	}

	if (cers.length === 0) {
		return (
			<StatusBlock
				kind="empty"
				size="lg"
				message="Non amministri ancora nessuna CER. Contatta un Super Admin per ottenere l'accesso."
			/>
		);
	}

	return (
		<Stack gap="xl">
			{/* Header: CER selector + new config */}
			<Row wrap justify="between" gap="md">
				<LabeledSelect label="CER" value={cerId} onChange={selectCer}>
					{cers.map((c) => (
						<option key={c.id} value={c.id}>
							{c.nome}
							{c.disabled ? ' (disabilitata)' : ''}
						</option>
					))}
				</LabeledSelect>
				<Button asChild variant="accent" size="sm">
					<Link href={`/configurations/new?cer=${cerId}`}>
						<Plus />
						Nuova Configurazione
					</Link>
				</Button>
			</Row>

			{/* Configurations list */}
			<ListCard header={configsLoading ? 'Configurazioni' : <>Configurazioni ({configs.length})</>}>
				{configsLoading ? (
					<ListCardSkeleton rows={3} />
				) : configs.length === 0 ? (
					<ListCardEmpty>
						Nessuna configurazione in questa CER. Creane una con “Nuova Configurazione”.
					</ListCardEmpty>
				) : (
					configs.map((config) => (
						<ListCardRow
							key={config.id}
							hover
							leading={<Layers />}
							leadingTone="purple"
							title={
								<>
									{config.nome}{' '}
									{config.disabled && <CardBadge tone="orange">Disabilitata</CardBadge>}
								</>
							}
							subtitle={
								<>
									{config.membersCount} soci &middot; {config.kwp} kWp
								</>
							}
							actions={
								<Row gap="sm">
									<Button
										asChild
										variant="outline"
										size="sm"
										title="Dettagli impianto e cabina primaria"
									>
										<Link href={`/configurations/${config.id}/details`}>Dettagli</Link>
									</Button>
									<Button asChild variant="ghost" size="icon" title="Modifica">
										<Link href={`/configurations/${config.id}`}>
											<Pencil />
										</Link>
									</Button>
									<Button
										type="button"
										variant="ghostDanger"
										size="icon"
										title="Elimina"
										onClick={() => setPendingDelete(config)}
									>
										<Trash2 />
									</Button>
								</Row>
							}
						/>
					))
				)}
			</ListCard>

			{/* Referents */}
			<ReferentsPanel cerId={cerId} />

			<ConfirmationDialog
				open={!!pendingDelete}
				onOpenChange={(open) => {
					if (!open) setPendingDelete(null);
				}}
				title="Eliminare la configurazione?"
				description={`Stai per eliminare "${pendingDelete?.nome}". L'azione è irreversibile.`}
				confirmText="Sì, elimina"
				cancelText="Annulla"
				onConfirm={confirmDelete}
				isLoading={deleting}
				variant="destructive"
			/>
		</Stack>
	);
}

function ReferentsPanel({ cerId }: { cerId: string }) {
	const [users, setUsers] = useState<CerUser[]>([]);
	const [usersLoading, setUsersLoading] = useState(true);
	const [updating, setUpdating] = useState<string | null>(null);
	const [open, setOpen] = useState(false);

	const load = useCallback(async () => {
		if (!cerId) {
			setUsersLoading(false);
			return;
		}
		setUsersLoading(true);
		try {
			const res = await apiFetch(`/api/admin/cers/${cerId}/users`);
			if (!res.ok) return;
			const data: unknown = await res.json();
			setUsers(Array.isArray(data) ? (data as CerUser[]) : []);
		} finally {
			setUsersLoading(false);
		}
	}, [cerId]);

	useEffect(() => {
		load();
	}, [load]);

	// A membership/role added or changed elsewhere (e.g. AddCerUser in another
	// tab, or a role edit on the member's own detail page) must reach this list
	// live, not just after the next navigation to this page.
	useEffect(() => {
		if (!cerId) return;
		return subscribeToTable(
			`user-org-memberships-cer-${cerId}`,
			{
				event: '*',
				schema: 'public',
				table: 'user_org_memberships',
				filter: `org_id=eq.${cerId}`,
			},
			() => {
				load();
			},
		);
	}, [cerId, load]);

	async function setRole(userId: string, role: 'referent' | 'member') {
		setUpdating(userId);
		try {
			const res = await apiFetch(`/api/admin/cers/${cerId}/users/${userId}`, {
				method: 'PATCH',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ role }),
			});
			if (res.ok) {
				setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)));
				toast.success('Ruolo aggiornato');
			} else {
				toast.error('Errore aggiornamento ruolo');
			}
		} finally {
			setUpdating(null);
		}
	}

	// Admins are managed at CER level (Super Admin); only show referents/members here.
	const assignable = users.filter((u) => u.role === 'referent' || u.role === 'member');

	return (
		<ListCard
			header={
				<>
					<Users /> Referenti
				</>
			}
			headerAside={
				<Button type="button" variant="accentSoft" size="sm" onClick={() => setOpen((v) => !v)}>
					<UserPlus /> Nuovo referente
				</Button>
			}
			subheader={
				open ? (
					<AddCerUser
						basePath={`/api/admin/cers/${cerId}`}
						role="referent"
						roleLabel="referente"
						onAdded={() => {
							setOpen(false);
							load();
						}}
					/>
				) : undefined
			}
		>
			{usersLoading ? (
				<ListCardSkeleton rows={2} />
			) : assignable.length === 0 ? (
				// Qui ci sono gli account con accesso alla CER, non i soci dell'anagrafica:
				// «nessun socio» compariva anche su CER con soci (INB-343).
				<ListCardEmpty>
					Nessun referente assegnato. I soci si gestiscono dal Registro soci.
				</ListCardEmpty>
			) : (
				assignable.map((u) => (
					<ListCardRow
						key={u.id}
						leading={
							<InitialAvatar
								tone="purple"
								initial={(u.fullName ?? u.email).charAt(0).toUpperCase()}
							/>
						}
						title={u.fullName ?? u.email}
						subtitle={u.fullName ? u.email : undefined}
						actions={
							<AdminSelect
								size="compact"
								value={u.role}
								disabled={updating === u.id}
								onChange={(v) => setRole(u.id, v as 'referent' | 'member')}
							>
								<option value="member">Socio</option>
								<option value="referent">Referente</option>
							</AdminSelect>
						}
					/>
				))
			)}
		</ListCard>
	);
}
